import { get, post, patch } from '../../helpers/api';

/**
 * Compatibility shim.
 *
 * `GET /admin/stations`, `GET /admin/stations/:id`, `POST /admin/stations`
 * and `PATCH /admin/stations/:id` all return the REST-shaped `AdminStation`
 * row (`apps/admin/src/v1/services/stations.service.ts:20`), unmapped by the
 * controller, but five downstream consumers (`StationMap`,
 * `EditStationLocation`, `PartnerTypeChip`, `PartnerTypeField`,
 * `AddStation`/`StationsTable`) were written against the old GraphQL
 * `station` shape and still read it that way. This function is the single
 * place that reconciles the two shapes so those call sites stay untouched.
 *
 * Remove this mapping once those consumers are modernised to read the REST
 * field names directly.
 *
 * IMPORTANT ASYMMETRY, do not "harmonise" the two directions — they are
 * genuinely different on the wire:
 *
 * - INBOUND (`toCompatShape`, below): the row's `id` is actually `_id`
 *   (Mongo), and `coord` already arrives as GeoJSON —
 *   `{ type: 'Point', coordinates: [longitude, latitude] }` — because the
 *   backend documents and emits it in exactly that shape. There are no
 *   `latitude`/`longitude` scalars inbound. `coord` is passed through
 *   UNCHANGED — no reordering, no synthesizing a new object — so that a
 *   null/absent `coord` stays falsy for the two consumers
 *   (`StationMap`, `EditStationLocation`) that guard on
 *   `station.coord` before reading `.coordinates`. An earlier version of
 *   this shim synthesized `coord` from `station.latitude`/`station.longitude`
 *   scalars that do not exist on this response, silently producing
 *   `coordinates: [undefined, undefined]` for every station (a `coord`
 *   object that is truthy but empty, which made those guards a no-op) — see
 *   the fix-round note in `StationsApi.test.js` and the task-1 report.
 * - OUTBOUND (`toRestPayload`, further down): the create/update DTOs
 *   (`StationRestCreateDto`/`StationRestUpdateDto`) genuinely DO want
 *   `latitude`/`longitude` scalars and `cityMongoId`, not GeoJSON/`city_id`.
 *   That half was correct from the start and is unchanged here.
 *
 * `state` and `zipCode` are available on the REST row but are deliberately
 * left off this shape — no consumer reads either field off a station.
 *
 * `createdAt`/`updatedAt` are INBOUND-ONLY, and belong to the asymmetry above:
 * they are read-only on the backend (`created_at`/`updated_at` take their column
 * defaults on INSERT and `@updatedAt` on UPDATE — see the note on
 * `StationService.updateStation` in hitch-services), and neither
 * `StationRestCreateDto` nor `StationRestUpdateDto` accepts them. Do NOT add
 * them to `toRestPayload`. They are passed through as the RAW STRINGS the wire
 * gives us — `AdminStation` types them `Date`, but JSON has no date type, so
 * what actually arrives is an ISO-8601 string. Anything calling `Date`/`moment`
 * methods on the value must parse it first; `useFormatters().date` does.
 *
 * Both are `undefined` until the hitch-services deploy carrying
 * 20260819000000_station_timestamps reaches the environment in use, so every
 * consumer must tolerate their absence.
 */
const toCompatShape = (station) => ({
  id: station._id ?? station.id,
  name: station.name,
  address: station.address,
  city: station.city,
  partnerType: station.partnerType,
  operational: station.operational,
  timeZone: station.timeZone,
  placeId: station.placeId,
  coord: station.coord,
  createdAt: station.createdAt,
  updatedAt: station.updatedAt,
});

/**
 * Reverse of `toCompatShape`, for `updateStation`/`createStation` payloads.
 *
 * Callers pass `{ coord: [lon, lat] }` (and `createStation` additionally
 * passes `city_id`), matching the old GraphQL input shape. The REST DTOs
 * want `latitude`/`longitude` scalars and `cityMongoId` instead (deliberate:
 * hitch-services #3459 aligned the REST DTOs to the candidate-generation
 * response shape). This is the single place that translates outward, so the
 * six call sites keep their current argument shapes. This direction was
 * verified against `StationRestCreateDto`/`StationRestUpdateDto` and is
 * correct as-is — see the asymmetry note on `toCompatShape` above before
 * changing anything here.
 *
 * Remove this mapping once those call sites are modernised to build
 * `latitude`/`longitude`/`cityMongoId` payloads directly.
 */
const toRestPayload = ({ coord, city_id: cityId, ...rest }) => {
  const payload = { ...rest };
  if (coord !== undefined && coord !== null) {
    const [longitude, latitude] = coord;
    payload.latitude = latitude;
    payload.longitude = longitude;
  }
  if (cityId !== undefined) {
    payload.cityMongoId = cityId;
  }
  return payload;
};

/**
 * `city` is a Mongo id (`@IsMongoId()` on the backend DTO) and is passed
 * through as-is — the frontend already carries a Mongo id in this param, so
 * there is no conversion to do, unlike `getCities`' `operational` filter.
 *
 * `offset`/`limit`/`city`/`q` are omitted from the query string entirely
 * when absent, rather than sent as empty values.
 *
 * `sortBy`/`sortDir` are omitted the same way. The backend allow-lists
 * `sortBy` to `name | createdAt | updatedAt` (`STATION_SORT_FIELDS` in
 * `libs/dao/station`) and 400s anything else, so this does NOT validate the
 * value — callers pick from that list. Omitting `sortBy` is meaningful rather
 * than merely tidy: it selects the backend's own default ordering (name
 * ascending, with an `id` tiebreak) instead of restating it here.
 *
 * `total` is the true total across all pages, and is `null` on the search
 * branch (when `q` is given) because that path runs no count query. It is
 * passed through unchanged — coercing it to a number would collapse the
 * pager to one page and hide the rest.
 */
const getStations = async ({ city, q, sortBy, sortDir, offset = 0, limit = 25 } = {}) => {
  const params = new URLSearchParams();
  if (city !== undefined) params.set('city', city);
  if (q !== undefined) params.set('q', q);
  if (sortBy !== undefined) params.set('sortBy', sortBy);
  if (sortDir !== undefined) params.set('sortDir', sortDir);
  params.set('offset', offset);
  params.set('limit', limit);

  const { data } = await get(`/admin/stations?${params.toString()}`);
  return { data: data.data.map(toCompatShape), count: data.count, total: data.total };
};

const getStation = async (id) => {
  const { data } = await get(`/admin/stations/${id}`);
  return toCompatShape(data);
};

const updateStation = async (id, input) => {
  const { data } = await patch(`/admin/stations/${id}`, toRestPayload(input));
  return toCompatShape(data);
};

const createStation = async ({ input }) => {
  const { data } = await post('/admin/stations', toRestPayload(input));
  return toCompatShape(data);
};

export default {
  getStations,
  getStation,
  updateStation,
  createStation,
};
